import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type ThHTMLAttributes,
} from "react";
import { twMerge } from "tailwind-merge";

export type TableContainerProps = HTMLAttributes<HTMLDivElement> & {
  isPrinting?: boolean;
  horizontalInfinite?: boolean;
  roundedBottom?: boolean;
  roundedTop?: boolean;
  style?: CSSProperties;
};

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  (
    {
      className,
      // Retained for Table API compatibility; scrollbar chrome lives on the body scroller.
      horizontalInfinite: _horizontalInfinite = false,
      isPrinting = false,
      roundedBottom = true,
      roundedTop = false,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={twMerge(
        isPrinting
          ? "border border-zinc-300 bg-white dark:border-zinc-800 dark:bg-zinc-900"
          : "border border-zinc-300 bg-white dark:border-zinc-800 dark:bg-zinc-900",
        "overflow-hidden",
        roundedBottom && "rounded-b-lg",
        roundedTop && "rounded-t-lg",
        className,
      )}
      {...props}
    />
  ),
);

export const TableLayout = forwardRef<
  HTMLTableElement,
  HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={twMerge("w-full bg-white dark:bg-zinc-900", className)}
    {...props}
  />
));

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={twMerge("bg-zinc-50 dark:bg-zinc-900", className)}
    {...props}
  />
));

export const TableHeaderRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={twMerge("bg-zinc-50 dark:bg-zinc-900", className)}
    {...props}
  />
));

export const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={twMerge(
      "flex items-center border-b border-zinc-300 bg-zinc-50 px-2 py-1 text-xs font-semibold dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-300",
      className,
    )}
    {...props}
  />
));

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
));

export type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  interactive?: boolean;
};

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive = false, ...props }, ref) => (
    <tr
      ref={ref}
      className={twMerge(
        "text-sm dark:text-zinc-300",
        interactive &&
          "cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50",
        className,
      )}
      {...props}
    />
  ),
);

export type TableCellProps = HTMLAttributes<HTMLTableCellElement> & {
  padded?: boolean;
};

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, padded = false, ...props }, ref) => (
    <td
      ref={ref}
      className={twMerge(
        "flex border-collapse items-center border border-t-0 border-r-0 border-l-0 border-zinc-300 dark:border-zinc-800",
        padded && "p-2",
        className,
      )}
      {...props}
    />
  ),
);

TableContainer.displayName = "TableContainer";
TableLayout.displayName = "TableLayout";
TableHeader.displayName = "TableHeader";
TableHeaderRow.displayName = "TableHeaderRow";
TableHeaderCell.displayName = "TableHeaderCell";
TableBody.displayName = "TableBody";
TableRow.displayName = "TableRow";
TableCell.displayName = "TableCell";

export const TableEmptyState = ({ children }: { children: ReactNode }) => (
  <div className="w-full border-b border-zinc-300 p-2 dark:border-zinc-800">
    <span className="text-sm dark:text-zinc-500">{children}</span>
  </div>
);
